import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Main_Loader, PageSidebar, PageTitle } from 'monica-alexandria';
import { Toolbar_ } from '../../partials/navigation/Toolbar_';
import { get_route_language } from '../../../helpers/routeLanguage';
import {
    offer_current_status
} from '../../../helpers/offerObject';
import { merge_offer_board_rows } from '../../../helpers/offerBoardRows';
import {
    list_legacy_board_offers,
    list_panel_board_offers,
    list_sass_board_offers
} from '../../../services/api/Offers';

const PanelContent = styled.div``

const PanelDashboard = styled.div`
    display: grid;
    gap: 1.2rem;

    @media (min-width: 72rem) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const PanelBoardCard = styled.button`
    display: grid;
    gap: 1.2rem;
    width: 100%;
    min-height: 22rem;
    padding: 1.4rem;
    border: 0.1rem solid ${p => p.theme.low};
    border-radius: var(--normalRadius);
    background: ${p => p.theme.background};
    color: ${p => p.theme.color};
    text-align: left;
    cursor: pointer;

    &:hover {
        border-color: var(--main);
        box-shadow: ${p => p.theme.out};
    }

    h3, h5, p {
        margin: 0;
    }
`;

const PanelBoardCardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
`;

const PanelBoardCardAction = styled.p`
    color: var(--main);
`;

const PanelBoardStats = styled.div`
    display: grid;
    gap: 0.8rem;
`;

const PanelBoardStat = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding-top: 0.8rem;
    border-top: 0.1rem solid ${p => p.theme.low};
`;

const read_key = (value = '') => {
    return `${value || ''}`.trim().toLowerCase();
};

const read_array = (value) => {
    return Array.isArray(value) ? value : [];
};

const board_rows_from_result = (result = {}) => {
    if (result.status !== 'fulfilled') {
        return [];
    }

    return result.value?.data?.data || [];
};

const count_rows = (rows = [], predicate = () => false) => {
    return rows.filter((rowItem) => predicate(rowItem)).length;
};

const timeline_statuses_from_offer = (offerItem = {}) => {
    return [
        ...read_array(offerItem?.statusline),
        ...read_array(offerItem?.timeline),
        ...read_array(offerItem?.activity?.timeline),
        ...read_array(offerItem?.niffler?.timeline)
    ].map((timelineItem) => read_key(timelineItem?.status)).filter(Boolean);
};

const payment_statuses_from_offer = (offerItem = {}) => {
    return [
        offer_current_status(offerItem),
        offerItem?.status,
        offerItem?.paymentStatus,
        offerItem?.payments?.recurring?.status,
        offerItem?.payments?.oneOff?.status,
        offerItem?.paymentState?.recurring,
        offerItem?.paymentState?.oneOff,
        offerItem?.paymentState?.one_off,
        ...timeline_statuses_from_offer(offerItem)
    ].map((statusKey) => read_key(statusKey)).filter(Boolean);
};

const offer_status_key = (offerItem = {}) => {
    return read_key(offer_current_status(offerItem) || offerItem?.status);
};

const offer_is_paid = (offerItem = {}) => {
    return payment_statuses_from_offer(offerItem).includes('paid');
};

const offer_is_initialised = (offerItem = {}) => {
    return payment_statuses_from_offer(offerItem).some((statusKey) => {
        return ['initialised', 'paid_initialised', 'payment_initialised'].includes(statusKey);
    });
};

const offer_is_invalid = (offerItem = {}) => {
    return ['invalid', 'expired', 'deleted', 'cancelled', 'lost'].includes(offer_status_key(offerItem));
};

const offer_needs_request_or_help = (offerItem = {}) => {
    const statusKey = offer_status_key(offerItem);
    const originKey = read_key(offerItem?.origin);

    return ['help', 'requested', 'request', 'preoffer', 'ready_for_review'].includes(statusKey)
        || ['help', 'request'].includes(originKey);
};

const sass_status_key = (offerItem = {}) => {
    const timelineStatus = timeline_statuses_from_offer(offerItem).pop();
    const statusKey = read_key(
        offerItem?.boardStatus
        || offerItem?.sassBoardStatus
        || offerItem?.setupData?.sassBoardStatus
        || offerItem?.typesData?.sassBoardStatus
        || timelineStatus
        || offer_current_status(offerItem)
        || offerItem?.status
    );
    const statusLookup = {
        meeting: 'in_progress',
        inprogress: 'in_progress',
        informer: 'paid',
        informed: 'paid'
    };

    return statusLookup[statusKey] || statusKey;
};

const sass_is_invalid = (offerItem = {}) => {
    return ['invalid', 'expired', 'deleted', 'cancelled', 'lost'].includes(sass_status_key(offerItem));
};

const agency_stats = (offers = [], t) => {
    return [
        { label: t('Total'), value: offers.length },
        { label: t('Valid'), value: count_rows(offers, (offerItem) => offer_status_key(offerItem) === 'valid') },
        { label: t('Paid'), value: count_rows(offers, offer_is_paid) },
        { label: t('Initialised'), value: count_rows(offers, offer_is_initialised) },
        { label: t('Requests'), value: count_rows(offers, offer_needs_request_or_help) },
        { label: t('Invalid'), value: count_rows(offers, offer_is_invalid) }
    ];
};

const sass_stats = (offers = [], t) => {
    return [
        { label: t('Total'), value: offers.length },
        { label: t('Valid'), value: count_rows(offers, (offerItem) => !sass_is_invalid(offerItem)) },
        { label: t('IN PROGRESS'), value: count_rows(offers, (offerItem) => sass_status_key(offerItem) === 'in_progress') },
        { label: t('Paid'), value: count_rows(offers, (offerItem) => sass_status_key(offerItem) === 'paid') },
        { label: t('New'), value: count_rows(offers, (offerItem) => !sass_status_key(offerItem).length) },
        { label: t('Invalid'), value: count_rows(offers, sass_is_invalid) }
    ];
};

const board_cards = (boards = {}, t) => {
    return [
        {
            key: 'offers',
            title: t('Offers'),
            path: '/agency',
            stats: agency_stats(boards.offers || [], t)
        },
        {
            key: 'sass',
            title: t('Sass'),
            path: '/sass',
            stats: sass_stats(boards.sass || [], t)
        }
    ];
};

export default function PANEL() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [sidebar, setSidebar] = useState(false);
    const [boards, setBoards] = useState({
        offers: [],
        sass: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const routeLanguage = get_route_language(i18n.language);
    const dashboardCards = board_cards(boards, t);

    const get_boards = async() => {
        setIsLoading(true);
        setErrorMessage('');

        const [offersResult, legacyResult, sassResult] = await Promise.allSettled([
            list_panel_board_offers(),
            list_legacy_board_offers(),
            list_sass_board_offers()
        ]);
        const hasFailedRequest = [offersResult, legacyResult, sassResult].some((result) => result.status === 'rejected');

        setBoards({
            offers: merge_offer_board_rows(
                board_rows_from_result(offersResult),
                board_rows_from_result(legacyResult)
            ),
            sass: board_rows_from_result(sassResult)
        });

        if (hasFailedRequest) {
            setErrorMessage(t('Could not load board stats.'));
        }

        setIsLoading(false);
    };

    useEffect(() => {
        get_boards();
    }, []);

    return (
        <PageSidebar>
            <Toolbar_
                sidebar={sidebar}
                onSidebar={() => setSidebar(!sidebar)}
                routeLanguage={routeLanguage}
                activeOption=""
            />
            <PanelContent>
                <PageTitle>
                    <h1>{t('Offers')}</h1>
                </PageTitle>
                {errorMessage ? <p>{errorMessage}</p> : null}
                {isLoading ? <Main_Loader></Main_Loader> : null}
                <PanelDashboard>
                    {dashboardCards.map((cardItem) => (
                        <PanelBoardCard
                            key={cardItem.key}
                            type="button"
                            onClick={() => navigate(`/${routeLanguage}${cardItem.path}`)}
                        >
                            <PanelBoardCardHeader>
                                <h3>{cardItem.title}</h3>
                                <PanelBoardCardAction>{cardItem.action || t('Open board')}</PanelBoardCardAction>
                            </PanelBoardCardHeader>
                            <PanelBoardStats>
                                {cardItem.stats.map((statItem) => (
                                    <PanelBoardStat key={`${cardItem.key}_${statItem.label}`}>
                                        <p>{statItem.label}</p>
                                        <h5>{statItem.value}</h5>
                                    </PanelBoardStat>
                                ))}
                            </PanelBoardStats>
                        </PanelBoardCard>
                    ))}
                </PanelDashboard>
            </PanelContent>
        </PageSidebar>
    );
}
